/**
 * Mutation hooks for HealthData operations
 * Replaces save() and update() methods on HealthData models
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useBackend } from './useBackend';
import { createHealthDataPoint, HealthDataPoint } from '../Model/HealthData/HealthDataPoint';
import { createHealthDataPointQuantity, HealthDataPointQuantity } from '../Model/HealthData/HealthDataPointQuantity';
import { createHealthDataPointCategory, HealthDataPointCategory } from '../Model/HealthData/HealthDataPointCategory';
import {
  createHealthDataPointCorrelation,
  HealthDataPointCorrelation,
} from '../Model/HealthData/HealthDataPointCorrelation';

export function useSaveHealthDataPoint() {
  const backend = useBackend();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dataPoint: HealthDataPoint) =>
      backend.post('/athlete/health-data', dataPoint).then((result) => {
        const updated = createHealthDataPoint({ ...dataPoint, id: result.id || dataPoint.id });
        return updated;
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['healthData'] });
      queryClient.invalidateQueries({ queryKey: ['athleteHealthData'] });
    },
  });
}

export function useUpdateHealthDataPoint() {
  const backend = useBackend();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dataPoint: HealthDataPoint) => backend.put(`/athlete/health-data/${dataPoint.id}`, dataPoint),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['healthData'] });
      queryClient.invalidateQueries({ queryKey: ['athleteHealthData'] });
    },
  });
}

export function useSaveHealthDataPointQuantity() {
  const backend = useBackend();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dataPoint: HealthDataPointQuantity) =>
      backend.post('/athlete/health-data', dataPoint).then((result) => {
        const updated = createHealthDataPointQuantity({ ...dataPoint, id: result.id || dataPoint.id });
        return updated;
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['healthData'] });
      queryClient.invalidateQueries({ queryKey: ['athleteHealthData'] });
    },
  });
}

export function useUpdateHealthDataPointQuantity() {
  const backend = useBackend();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dataPoint: HealthDataPointQuantity) =>
      backend.put(`/athlete/health-data/${dataPoint.id}`, dataPoint),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['healthData'] });
      queryClient.invalidateQueries({ queryKey: ['athleteHealthData'] });
    },
  });
}

export function useSaveHealthDataPointCategory() {
  const backend = useBackend();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dataPoint: HealthDataPointCategory) =>
      backend.post('/athlete/health-data', dataPoint).then((result) => {
        const updated = createHealthDataPointCategory({ ...dataPoint, id: result.id || dataPoint.id });
        return updated;
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['healthData'] });
      queryClient.invalidateQueries({ queryKey: ['athleteHealthData'] });
    },
  });
}

export function useUpdateHealthDataPointCategory() {
  const backend = useBackend();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dataPoint: HealthDataPointCategory) =>
      backend.put(`/athlete/health-data/${dataPoint.id}`, dataPoint),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['healthData'] });
      queryClient.invalidateQueries({ queryKey: ['athleteHealthData'] });
    },
  });
}

export function useSaveHealthDataPointCorrelation() {
  const backend = useBackend();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dataPoint: HealthDataPointCorrelation) =>
      backend.post('/athlete/health-data', dataPoint).then((result) => {
        const updated = createHealthDataPointCorrelation({ ...dataPoint, id: result.id || dataPoint.id });
        return updated;
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['healthData'] });
      queryClient.invalidateQueries({ queryKey: ['athleteHealthData'] });
    },
  });
}

export function useUpdateHealthDataPointCorrelation() {
  const backend = useBackend();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dataPoint: HealthDataPointCorrelation) =>
      backend.put(`/athlete/health-data/${dataPoint.id}`, dataPoint),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['healthData'] });
      queryClient.invalidateQueries({ queryKey: ['athleteHealthData'] });
    },
  });
}

export function useSaveHealthForm() {
  const backend = useBackend();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (form: any) => backend.post('/athlete/health-data', form),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['healthData'] });
      queryClient.invalidateQueries({ queryKey: ['healthForm'] });
    },
  });
}
